import React from 'react';
import {
  Box,
  Text,
  HStack,
  VStack,
  Badge,
  Icon,
  useColorModeValue,
  Collapse,
  useDisclosure,
} from '@chakra-ui/react';
import {
  TrendingUp,
  AlertTriangle,
  GitCompare,
  Bell,
  Lightbulb,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Insight, InsightType, RelevanceLevel } from '../../types/chat';

interface InsightCardProps {
  insight: Insight;
}

const getInsightIcon = (tipo: InsightType) => {
  switch (tipo) {
    case 'tendencia':
      return TrendingUp;
    case 'anomalia':
      return AlertTriangle;
    case 'comparacao':
      return GitCompare;
    case 'alerta':
      return Bell;
    case 'recomendacao':
      return Lightbulb;
    default:
      return Lightbulb;
  }
};

const getInsightColor = (tipo: InsightType) => {
  switch (tipo) {
    case 'tendencia':
      return 'blue';
    case 'anomalia':
      return 'orange';
    case 'comparacao':
      return 'purple';
    case 'alerta':
      return 'red';
    case 'recomendacao':
      return 'green';
    default:
      return 'gray';
  }
};

const getRelevanceColor = (relevancia: RelevanceLevel) => {
  switch (relevancia) {
    case 'alta':
      return 'red';
    case 'media':
      return 'yellow';
    case 'baixa':
      return 'green';
    default:
      return 'gray';
  }
};

const getRelevanceLabel = (relevancia: RelevanceLevel) => {
  switch (relevancia) {
    case 'alta':
      return 'Alta';
    case 'media':
      return 'Média';
    case 'baixa':
      return 'Baixa';
    default:
      return 'Normal';
  }
};

const getTipoLabel = (tipo: InsightType) => {
  switch (tipo) {
    case 'tendencia':
      return 'Tendência';
    case 'anomalia':
      return 'Anomalia';
    case 'comparacao':
      return 'Comparação';
    case 'alerta':
      return 'Alerta';
    case 'recomendacao':
      return 'Recomendação';
    default:
      return tipo;
  }
};

export const InsightCard: React.FC<InsightCardProps> = ({ insight }) => {
  const { isOpen, onToggle } = useDisclosure();
  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.600');

  const IconComponent = getInsightIcon(insight.tipo);
  const colorScheme = getInsightColor(insight.tipo);
  const relevanceColor = getRelevanceColor(insight.relevancia);

  const hasSupportData = insight.dados_suporte && Object.keys(insight.dados_suporte).length > 0;

  return (
    <Box
      bg={bgColor}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="md"
      p={4}
      mb={3}
      transition="all 0.2s"
      _hover={{ bg: hoverBg, transform: 'translateY(-2px)', shadow: 'md' }}
    >
      <VStack align="stretch" spacing={2}>
        {/* Header */}
        <HStack justify="space-between">
          <HStack spacing={2}>
            <Icon as={IconComponent} color={`${colorScheme}.500`} boxSize={5} />
            <Badge colorScheme={colorScheme} fontSize="xs">
              {getTipoLabel(insight.tipo)}
            </Badge>
            <Badge colorScheme={relevanceColor} fontSize="xs" variant="outline">
              {getRelevanceLabel(insight.relevancia)}
            </Badge>
          </HStack>
          {hasSupportData && (
            <Icon
              as={isOpen ? ChevronUp : ChevronDown}
              cursor="pointer"
              onClick={onToggle}
              color="gray.500"
              _hover={{ color: 'gray.700' }}
            />
          )}
        </HStack>

        {/* Título */}
        <Text fontWeight="bold" fontSize="md">
          {insight.titulo}
        </Text>

        {/* Descrição */}
        <Text fontSize="sm" color="gray.600">
          {insight.descricao}
        </Text>

        {/* Dados de Suporte (Expansível) */}
        {hasSupportData && (
          <Collapse in={isOpen} animateOpacity>
            <Box
              mt={2}
              p={3}
              bg={useColorModeValue('gray.50', 'gray.800')}
              borderRadius="md"
              fontSize="sm"
            >
              <Text fontWeight="semibold" mb={2} fontSize="xs" color="gray.500">
                DADOS DE SUPORTE
              </Text>
              <VStack align="stretch" spacing={1}>
                {Object.entries(insight.dados_suporte || {}).map(([key, value]) => (
                  <HStack key={key} justify="space-between">
                    <Text fontWeight="medium">{key}:</Text>
                    <Text color="gray.600">
                      {typeof value === 'number' ? value.toLocaleString('pt-BR') : String(value)}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            </Box>
          </Collapse>
        )}
      </VStack>
    </Box>
  );
};
